/**
 * Worksheet Builder Service
 *
 * CRUD operations for custom worksheets created via the builder.
 * Uses the existing API client pattern from the project.
 */

import { api } from "./api-client";
import type { WorksheetTemplate } from "@/components/builder/types";

// ============================================================================
// Types - API Response shapes
// ============================================================================

export interface WorksheetTemplateListResponse {
  items: WorksheetTemplate[];
  total: number;
}

export interface WorksheetTemplateResponse {
  worksheet: WorksheetTemplate;
}

export interface SaveWorksheetRequest {
  title: string;
  description?: string;
  grade: string[];
  subject: string;
  outcomeIds: string[];
  difficulty?: number;
  estimatedMinutes?: number;
  usageFrequency?: "daily" | "weekly" | "monthly" | "once";
  teacherNotes?: string;
  components: WorksheetTemplate["components"];
  layout: WorksheetTemplate["layout"];
  gradingConfig?: WorksheetTemplate["gradingConfig"];
  status?: "draft" | "published";
}

// ============================================================================
// Service Class
// ============================================================================

class WorksheetBuilderService {
  /**
   * Get all custom worksheets created by the current user.
   */
  async getMyWorksheets(signal?: AbortSignal): Promise<WorksheetTemplateListResponse> {
    return api.get<WorksheetTemplateListResponse>("/worksheets/builder/my", {
      signal,
    });
  }

  /**
   * Get a specific custom worksheet by ID.
   */
  async getWorksheet(
    id: string,
    signal?: AbortSignal
  ): Promise<WorksheetTemplate> {
    const response = await api.get<WorksheetTemplateResponse>(
      `/worksheets/builder/${id}`,
      { signal }
    );
    return response.worksheet;
  }

  /**
   * Create a new custom worksheet.
   */
  async createWorksheet(
    data: SaveWorksheetRequest,
    signal?: AbortSignal
  ): Promise<WorksheetTemplate> {
    const response = await api.post<WorksheetTemplateResponse>(
      "/worksheets/builder",
      data,
      { signal }
    );
    return response.worksheet;
  }

  /**
   * Update an existing custom worksheet.
   */
  async updateWorksheet(
    id: string,
    data: Partial<SaveWorksheetRequest>,
    signal?: AbortSignal
  ): Promise<WorksheetTemplate> {
    const response = await api.put<WorksheetTemplateResponse>(
      `/worksheets/builder/${id}`,
      data,
      { signal }
    );
    return response.worksheet;
  }

  /**
   * Delete a custom worksheet.
   */
  async deleteWorksheet(id: string, signal?: AbortSignal): Promise<void> {
    await api.delete(`/worksheets/builder/${id}`, { signal });
  }

  /**
   * Duplicate an existing worksheet (creates a copy as draft).
   */
  async duplicateWorksheet(
    id: string,
    signal?: AbortSignal
  ): Promise<WorksheetTemplate> {
    const response = await api.post<WorksheetTemplateResponse>(
      `/worksheets/builder/${id}/duplicate`,
      {},
      { signal }
    );
    return response.worksheet;
  }

  /**
   * Generate a preview PDF for a worksheet (returns URL).
   */
  getPreviewPdfUrl(id: string): string {
    const baseUrl =
      import.meta.env.VITE_API_BASE_URL || "http://localhost:8082";
    return `${baseUrl}/worksheets/builder/${id}/preview`;
  }

  /**
   * Publish a draft worksheet (makes it available for use).
   */
  async publishWorksheet(
    id: string,
    signal?: AbortSignal
  ): Promise<WorksheetTemplate> {
    const response = await api.post<WorksheetTemplateResponse>(
      `/worksheets/builder/${id}/publish`,
      {},
      { signal }
    );
    return response.worksheet;
  }

  /**
   * Unpublish a worksheet (reverts to draft).
   */
  async unpublishWorksheet(
    id: string,
    signal?: AbortSignal
  ): Promise<WorksheetTemplate> {
    const response = await api.post<WorksheetTemplateResponse>(
      `/worksheets/builder/${id}/unpublish`,
      {},
      { signal }
    );
    return response.worksheet;
  }

  /**
   * Auto-save a worksheet (called periodically during editing).
   * Uses PATCH for partial updates, doesn't change published status.
   */
  async autoSave(
    id: string,
    data: Partial<SaveWorksheetRequest>,
    signal?: AbortSignal
  ): Promise<WorksheetTemplate> {
    const response = await api.patch<WorksheetTemplateResponse>(
      `/worksheets/builder/${id}`,
      data,
      { signal }
    );
    return response.worksheet;
  }
}

export const worksheetBuilderService = new WorksheetBuilderService();
